import type { Location } from "react-router";
import { ROLE_LABELS, type RoleLabel } from "@app/lib/role-constants";

type AuthNavigationState = {
  from?: {
    pathname?: string;
    search?: string;
  };
  redirectTo?: string;
};

function isSafeInternalPath(pathname: string): boolean {
  return pathname.startsWith("/") && !pathname.startsWith("//");
}

const ADMIN_ONLY_PATH_PREFIXES = [
  "/admin/",
  "/dashboard",
  "/clients",
  "/organizers",
  "/settings",
] as const;

const ORGANIZER_ONLY_PATH_PREFIXES = ["/manage-events"] as const;

function isOrganizerOnlyPath(pathname: string): boolean {
  if (pathname.endsWith("/landing-page")) {
    return false;
  }

  return (
    ORGANIZER_ONLY_PATH_PREFIXES.some((prefix) =>
      pathname.startsWith(prefix),
    ) || pathname.startsWith("/events/")
  );
}

function canRoleAccessPath(pathname: string, roleLabel: RoleLabel): boolean {
  if (roleLabel === ROLE_LABELS.ORGANIZER) {
    return !ADMIN_ONLY_PATH_PREFIXES.some((prefix) =>
      pathname.startsWith(prefix),
    );
  }

  if (roleLabel === ROLE_LABELS.ADMIN) {
    return !isOrganizerOnlyPath(pathname);
  }

  return (
    !ADMIN_ONLY_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix)) &&
    !isOrganizerOnlyPath(pathname)
  );
}

export function getDefaultPathForRole(roleLabel: RoleLabel): string {
  if (roleLabel === ROLE_LABELS.ADMIN) {
    return "/admin/dashboard";
  }

  if (roleLabel === ROLE_LABELS.ORGANIZER) {
    return "/manage-events";
  }

  return "/";
}

export function resolvePostAuthPath(
  location: Location,
  defaultPath = "/manage-events",
  roleLabel?: RoleLabel,
): string {
  const state = location.state as AuthNavigationState | null;
  const effectiveRoleLabel = roleLabel ?? ROLE_LABELS.UNKNOWN;

  if (
    state?.redirectTo &&
    isSafeInternalPath(state.redirectTo) &&
    canRoleAccessPath(state.redirectTo, effectiveRoleLabel)
  ) {
    return state.redirectTo;
  }

  if (
    state?.from?.pathname &&
    isSafeInternalPath(state.from.pathname) &&
    canRoleAccessPath(state.from.pathname, effectiveRoleLabel)
  ) {
    return `${state.from.pathname}${state.from.search ?? ""}`;
  }

  const returnToParam = new URLSearchParams(location.search).get("returnTo");

  if (
    returnToParam &&
    isSafeInternalPath(returnToParam) &&
    canRoleAccessPath(returnToParam, effectiveRoleLabel)
  ) {
    return returnToParam;
  }

  return defaultPath;
}
